import PptxGenJS from 'pptxgenjs';
import { formatFilterSummary, FilterSummary } from './exportView';
import type { Initiative, Product, Quarter, Classification, Status } from '@/types';

// ── Layout constants (inches) ──────────────────────────────────────────────────
const SLIDE_W = 13.33;
const SLIDE_H = 7.5;
const MARGIN = 0.4;

// Header area
const HEADER_H = 0.45;
const HEADER_Y = MARGIN;

// Content area
const CONTENT_Y = HEADER_Y + HEADER_H + 0.15;
const LEGEND_H = 0.4;
const CONTENT_BOTTOM = SLIDE_H - MARGIN - LEGEND_H;
const CONTENT_H = CONTENT_BOTTOM - CONTENT_Y;

// Timeline-specific
const NAME_COL_W = 2.8;
const QUARTERS_PER_SLIDE = 4;
const QUARTER_COL_W = (SLIDE_W - MARGIN * 2 - NAME_COL_W) / QUARTERS_PER_SLIDE; // ~2.53"

// Initiative bar
const BAR_H = 0.3;
const BAR_GAP = 0.06;
const ROW_PAD = 0.1;

// Row headers
const PRODUCT_ROW_H = 0.35;
const SUBPRODUCT_ROW_H = 0.3;

// Colors
const BG_WHITE = 'FFFFFF';
const HEADER_BG = 'F1F5F9';
const PRODUCT_BG = 'EFF6FF';
const SUBPROD_BG = 'F8FAFC';
const TEXT_PRIMARY = '111827';
const TEXT_SECONDARY = '6B7280';
const TEXT_TERTIARY = '9CA3AF';
const BORDER_COLOR = 'E5E7EB';
const LINK_ICON_COLOR = '6B7280';

// ── Types ───────────────────────────────────────────────────────────────────────
interface ProductGroup {
  product: Product;
  subProducts: {
    name: string;
    initiatives: Initiative[];
  }[];
}

export interface PptxExportOptions {
  viewType: 'timeline' | 'list';
  filterSummary: FilterSummary;
  initiatives: Initiative[];
  products: Product[];
  quarters: Quarter[];
  classifications: Classification[];
  statuses: Status[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

function getTimestamp(): string {
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const time = now.toTimeString().split(' ')[0].replace(/:/g, '');
  return `${date}_${time}`;
}

/** Blend a hex color with white at a given opacity (0-1) */
function blendWithWhite(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const br = Math.round(r * opacity + 255 * (1 - opacity));
  const bg = Math.round(g * opacity + 255 * (1 - opacity));
  const bb = Math.round(b * opacity + 255 * (1 - opacity));
  return [br, bg, bb].map(c => c.toString(16).padStart(2, '0')).join('');
}

/** Strip leading # from hex color */
function hex(color: string): string {
  return color.replace(/^#/, '');
}

/** Group initiatives by product → sub-product hierarchy */
function groupByProduct(
  initiatives: Initiative[],
  products: Product[],
): ProductGroup[] {
  const initBySubProduct = new Map<string, Initiative[]>();
  initiatives.forEach(init => {
    const key = init.subProductId;
    if (!initBySubProduct.has(key)) initBySubProduct.set(key, []);
    initBySubProduct.get(key)!.push(init);
  });

  const groups: ProductGroup[] = [];
  for (const product of products) {
    const subGroups: ProductGroup['subProducts'] = [];
    for (const sp of product.subProducts || []) {
      const inits = initBySubProduct.get(sp.id);
      if (inits && inits.length > 0) {
        subGroups.push({ name: sp.name, initiatives: inits });
      }
    }
    if (subGroups.length > 0) {
      groups.push({ product, subProducts: subGroups });
    }
  }
  return groups;
}

// ── Slide builders ──────────────────────────────────────────────────────────────

/** Add header bar to a slide */
function addSlideHeader(
  slide: PptxGenJS.Slide,
  viewType: 'timeline' | 'list',
  filterSummary: FilterSummary,
  pageLabel?: string,
) {
  const title = `CrossProd \u2014 ${viewType === 'timeline' ? 'Timeline' : 'List'} View`;
  const filters = `Filters: ${formatFilterSummary(filterSummary)}`;
  const page = pageLabel || '';

  // Title left
  slide.addText(title, {
    x: MARGIN,
    y: HEADER_Y,
    w: SLIDE_W * 0.5,
    h: HEADER_H,
    fontSize: 12,
    fontFace: 'Arial',
    bold: true,
    color: TEXT_PRIMARY,
    valign: 'middle',
  });

  // Page right
  if (page) {
    slide.addText(page, {
      x: SLIDE_W * 0.5,
      y: HEADER_Y,
      w: SLIDE_W * 0.5 - MARGIN,
      h: HEADER_H,
      fontSize: 9,
      fontFace: 'Arial',
      color: TEXT_TERTIARY,
      align: 'right',
      valign: 'middle',
    });
  }

  // Filter summary below title
  slide.addText(filters, {
    x: MARGIN,
    y: HEADER_Y + HEADER_H * 0.65,
    w: SLIDE_W - MARGIN * 2,
    h: 0.2,
    fontSize: 7,
    fontFace: 'Arial',
    color: TEXT_SECONDARY,
    valign: 'top',
  });

  // Separator line
  slide.addShape('line' as PptxGenJS.ShapeType, {
    x: MARGIN,
    y: CONTENT_Y - 0.08,
    w: SLIDE_W - MARGIN * 2,
    h: 0,
    line: { color: BORDER_COLOR, width: 0.5 },
  });
}

/** Add classification + status legend to bottom of slide */
function addLegend(
  slide: PptxGenJS.Slide,
  classifications: Classification[],
  statuses: Status[],
) {
  const y = CONTENT_BOTTOM + 0.05;
  const items: PptxGenJS.TextProps[] = [
    {
      text: 'CLASSIFICATION:  ',
      options: { fontSize: 7, fontFace: 'Arial', color: TEXT_SECONDARY, bold: true },
    },
  ];

  classifications
    .filter(c => c.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((cls, i) => {
      if (i > 0) {
        items.push({
          text: '    ',
          options: { fontSize: 7 },
        });
      }
      items.push({
        text: '\u25A0 ',
        options: { fontSize: 9, color: hex(cls.color), fontFace: 'Arial' },
      });
      items.push({
        text: cls.name,
        options: { fontSize: 7, fontFace: 'Arial', color: TEXT_PRIMARY },
      });
    });

  // Separator
  items.push({
    text: '        STATUS:  ',
    options: { fontSize: 7, fontFace: 'Arial', color: TEXT_SECONDARY, bold: true },
  });

  statuses
    .filter(s => s.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((st, i) => {
      if (i > 0) {
        items.push({
          text: '    ',
          options: { fontSize: 7 },
        });
      }
      items.push({
        text: '\u25CF ',
        options: { fontSize: 8, color: hex(st.color), fontFace: 'Arial' },
      });
      items.push({
        text: st.name,
        options: { fontSize: 7, fontFace: 'Arial', color: TEXT_PRIMARY },
      });
    });

  slide.addText(items, {
    x: MARGIN,
    y,
    w: SLIDE_W - MARGIN * 2,
    h: LEGEND_H,
    valign: 'middle',
  });
}

// ── Timeline View ───────────────────────────────────────────────────────────────

function buildTimelineSlides(
  pptx: PptxGenJS,
  options: PptxExportOptions,
) {
  const { initiatives, products, quarters, classifications, statuses, filterSummary } = options;
  const groups = groupByProduct(initiatives, products);

  // Sort initiatives within each sub-product by startQuarter
  for (const group of groups) {
    for (const sp of group.subProducts) {
      sp.initiatives.sort((a, b) => {
        const aIdx = quarters.findIndex(q => q.key === a.startQuarter);
        const bIdx = quarters.findIndex(q => q.key === b.startQuarter);
        return aIdx - bIdx;
      });
    }
  }

  // Determine quarter chunks (4 per slide horizontally)
  const totalQuarters = quarters.length;
  const qChunks: Quarter[][] = [];
  for (let i = 0; i < totalQuarters; i += QUARTERS_PER_SLIDE) {
    qChunks.push(quarters.slice(i, i + QUARTERS_PER_SLIDE));
  }

  // Track slide count for page labels (two-pass: count first, then render)
  // We use render-as-you-go to handle overflow, so page numbers are assigned sequentially
  let slideNum = 0;

  for (const qChunk of qChunks) {
    const qStartIdx = quarters.indexOf(qChunk[0]);
    const colW = qChunk.length < QUARTERS_PER_SLIDE
      ? QUARTER_COL_W
      : (SLIDE_W - MARGIN * 2 - NAME_COL_W) / qChunk.length;
    const gridW = qChunk.length * colW;

    // --- Slide state ---
    let slide: PptxGenJS.Slide | null = null;
    let curY = 0;
    let currentProductIdOnSlide: string | null = null;

    /** Create a new slide with header, legend, column headers, and quarter dividers */
    function startNewSlide() {
      slideNum++;
      slide = pptx.addSlide();
      slide!.background = { fill: BG_WHITE };
      addSlideHeader(slide!, 'timeline', filterSummary);
      addLegend(slide!, classifications, statuses);

      const headerY = CONTENT_Y;

      // "Product / Initiative" header cell
      slide!.addText('Product / Initiative', {
        x: MARGIN,
        y: headerY,
        w: NAME_COL_W,
        h: PRODUCT_ROW_H,
        fontSize: 8,
        fontFace: 'Arial',
        bold: true,
        color: TEXT_PRIMARY,
        fill: { color: HEADER_BG },
        valign: 'middle',
        paraSpaceAfter: 0,
        margin: [0, 0, 0, 6],
      });

      // Quarter header cells
      qChunk.forEach((q, qi) => {
        slide!.addText(q.fullLabel, {
          x: MARGIN + NAME_COL_W + qi * colW,
          y: headerY,
          w: colW,
          h: PRODUCT_ROW_H,
          fontSize: 8,
          fontFace: 'Arial',
          bold: true,
          color: TEXT_PRIMARY,
          fill: { color: HEADER_BG },
          align: 'center',
          valign: 'middle',
        });
      });

      // Vertical quarter dividers
      for (let qi = 1; qi < qChunk.length; qi++) {
        slide!.addShape('line' as PptxGenJS.ShapeType, {
          x: MARGIN + NAME_COL_W + qi * colW,
          y: headerY + PRODUCT_ROW_H,
          w: 0,
          h: CONTENT_BOTTOM - headerY - PRODUCT_ROW_H,
          line: { color: 'F3F4F6', width: 0.5 },
        });
      }

      curY = headerY + PRODUCT_ROW_H;
      currentProductIdOnSlide = null;
    }

    /** Render a product header row */
    function renderProductHeader(group: ProductGroup) {
      const prodCount = group.subProducts.reduce((s, sp) => s + sp.initiatives.length, 0);
      slide!.addText(`${group.product.name} (${prodCount})`, {
        x: MARGIN,
        y: curY,
        w: NAME_COL_W + gridW,
        h: PRODUCT_ROW_H,
        fontSize: 8,
        fontFace: 'Arial',
        bold: true,
        color: TEXT_PRIMARY,
        fill: { color: PRODUCT_BG },
        valign: 'middle',
        margin: [0, 0, 0, 6],
      });
      curY += PRODUCT_ROW_H;
      currentProductIdOnSlide = group.product.id;
    }

    /** Render a sub-product header row */
    function renderSubProductHeader(name: string) {
      slide!.addText(name, {
        x: MARGIN,
        y: curY,
        w: NAME_COL_W + gridW,
        h: SUBPRODUCT_ROW_H,
        fontSize: 7.5,
        fontFace: 'Arial',
        bold: false,
        color: TEXT_SECONDARY,
        fill: { color: SUBPROD_BG },
        valign: 'middle',
        margin: [0, 0, 0, 14],
      });
      curY += SUBPRODUCT_ROW_H;
    }

    /** Render a single initiative bar */
    function renderBar(init: Initiative, barIndex: number, barAreaY: number) {
      const startIdx = quarters.findIndex(q => q.key === init.startQuarter);
      const endIdx = init.endQuarter
        ? quarters.findIndex(q => q.key === init.endQuarter)
        : startIdx;

      const chunkStart = qStartIdx;
      const chunkEnd = qStartIdx + qChunk.length - 1;

      // Skip if initiative doesn't overlap this chunk
      if (endIdx < chunkStart || startIdx > chunkEnd) return;

      // Clamp bar to visible quarters
      const barStart = Math.max(startIdx - chunkStart, 0);
      const barEnd = Math.min(endIdx - chunkStart, qChunk.length - 1);
      const barSpan = barEnd - barStart + 1;

      const barX = MARGIN + NAME_COL_W + barStart * colW + 0.04;
      const barY = barAreaY + barIndex * (BAR_H + BAR_GAP);
      const barW = barSpan * colW - 0.08;

      const clsColor = hex(init.classification?.color || '3B82F6');
      const bgColor = blendWithWhite(clsColor, 0.12);
      const statusColor = hex(init.status?.color || '6B7280');
      const hasDeps = init.dependencies && init.dependencies.length > 0;

      // Bar background
      slide!.addShape('roundRect' as PptxGenJS.ShapeType, {
        x: barX,
        y: barY,
        w: barW,
        h: BAR_H,
        rectRadius: 0.04,
        fill: { color: bgColor },
        line: { color: clsColor, width: 0.5 },
      });

      // Classification color left accent
      slide!.addShape('rect' as PptxGenJS.ShapeType, {
        x: barX,
        y: barY,
        w: 0.06,
        h: BAR_H,
        fill: { color: clsColor },
        line: { width: 0 },
      });

      // Status dot
      slide!.addText('\u25CF', {
        x: barX + 0.1,
        y: barY,
        w: 0.1,
        h: BAR_H,
        fontSize: 7,
        fontFace: 'Arial',
        color: statusColor,
        valign: 'middle',
        margin: 0,
      });

      // Title text
      const titleX = barX + 0.22;
      const depSpace = hasDeps ? 0.2 : 0;
      const titleW = barW - 0.26 - depSpace;

      if (titleW > 0.1) {
        slide!.addText(init.title, {
          x: titleX,
          y: barY,
          w: titleW,
          h: BAR_H,
          fontSize: 7,
          fontFace: 'Arial',
          color: TEXT_PRIMARY,
          valign: 'middle',
          margin: 0,
        });
      }

      // Dependency chain icon
      if (hasDeps) {
        slide!.addText('\u{1F517}', {
          x: barX + barW - 0.22,
          y: barY,
          w: 0.18,
          h: BAR_H,
          fontSize: 7,
          fontFace: 'Arial',
          color: LINK_ICON_COLOR,
          align: 'center',
          valign: 'middle',
          margin: 0,
        });
      }
    }

    // --- Render groups, splitting across slides as needed ---
    startNewSlide();

    for (const group of groups) {
      for (const sp of group.subProducts) {
        const initCount = sp.initiatives.length;
        const spHeight = SUBPRODUCT_ROW_H + ROW_PAD + initCount * (BAR_H + BAR_GAP) + ROW_PAD;
        const needsProductHeader = currentProductIdOnSlide !== group.product.id;
        const totalNeeded = spHeight + (needsProductHeader ? PRODUCT_ROW_H : 0);

        // If this sub-product won't fit and we already have content, start a new slide
        if (curY + totalNeeded > CONTENT_BOTTOM && curY > CONTENT_Y + PRODUCT_ROW_H) {
          startNewSlide();
        }

        // Render product header if needed
        if (currentProductIdOnSlide !== group.product.id) {
          renderProductHeader(group);
        }

        // Render sub-product header
        renderSubProductHeader(sp.name);

        // Render initiative bars, splitting across slides if there are too many
        let initIdx = 0;
        while (initIdx < initCount) {
          const availableH = CONTENT_BOTTOM - curY - ROW_PAD;
          const maxBars = Math.floor((availableH - ROW_PAD) / (BAR_H + BAR_GAP));

          if (maxBars <= 0) {
            // No room left — start a new slide and repeat headers
            startNewSlide();
            renderProductHeader(group);
            renderSubProductHeader(sp.name);
            continue; // recalculate available space
          }

          const barsThisPage = Math.min(Math.max(maxBars, 1), initCount - initIdx);
          const barAreaY = curY + ROW_PAD;

          for (let i = 0; i < barsThisPage; i++) {
            renderBar(sp.initiatives[initIdx + i], i, barAreaY);
          }

          curY += ROW_PAD + barsThisPage * (BAR_H + BAR_GAP) + ROW_PAD;
          initIdx += barsThisPage;
        }
      }
    }
  }
}

// ── List View ───────────────────────────────────────────────────────────────────

// List column widths (inches)
const LIST_COL_NAME_W = 4.5;
const LIST_COL_CLASS_W = 1.5;
const LIST_COL_STATUS_W = 1.5;
const LIST_COL_START_W = 1.3;
const LIST_COL_END_W = 1.3;
const LIST_TABLE_W = LIST_COL_NAME_W + LIST_COL_CLASS_W + LIST_COL_STATUS_W + LIST_COL_START_W + LIST_COL_END_W;
const LIST_ROW_H = 0.32;

function buildListSlides(
  pptx: PptxGenJS,
  options: PptxExportOptions,
) {
  const { initiatives, products, quarters, classifications, statuses, filterSummary } = options;
  const groups = groupByProduct(initiatives, products);

  // Sort initiatives within each sub-product by startQuarter
  for (const group of groups) {
    for (const sp of group.subProducts) {
      sp.initiatives.sort((a, b) => {
        const aIdx = quarters.findIndex(q => q.key === a.startQuarter);
        const bIdx = quarters.findIndex(q => q.key === b.startQuarter);
        return aIdx - bIdx;
      });
    }
  }

  // Flatten into rows for pagination
  type Row =
    | { type: 'product'; product: Product; count: number }
    | { type: 'subproduct'; name: string }
    | { type: 'initiative'; initiative: Initiative };

  const allRows: Row[] = [];
  for (const group of groups) {
    const count = group.subProducts.reduce((s, sp) => s + sp.initiatives.length, 0);
    allRows.push({ type: 'product', product: group.product, count });
    for (const sp of group.subProducts) {
      allRows.push({ type: 'subproduct', name: sp.name });
      for (const init of sp.initiatives) {
        allRows.push({ type: 'initiative', initiative: init });
      }
    }
  }

  // Paginate rows
  const headerRowH = LIST_ROW_H;
  const rowsPerSlide = Math.floor((CONTENT_H - headerRowH) / LIST_ROW_H);
  const pages: Row[][] = [];
  for (let i = 0; i < allRows.length; i += rowsPerSlide) {
    pages.push(allRows.slice(i, i + rowsPerSlide));
  }

  const totalSlides = pages.length;

  pages.forEach((pageRows, pageIdx) => {
    const slide = pptx.addSlide();
    slide.background = { fill: BG_WHITE };

    const pageLabel = totalSlides > 1 ? `Page ${pageIdx + 1} of ${totalSlides}` : '';
    addSlideHeader(slide, 'list', filterSummary, pageLabel);
    addLegend(slide, classifications, statuses);

    let curY = CONTENT_Y;
    const tableX = MARGIN;

    // Table column header
    const headerCols: { text: string; w: number }[] = [
      { text: 'Initiative Name', w: LIST_COL_NAME_W },
      { text: 'Classification', w: LIST_COL_CLASS_W },
      { text: 'Status', w: LIST_COL_STATUS_W },
      { text: 'Start', w: LIST_COL_START_W },
      { text: 'End', w: LIST_COL_END_W },
    ];

    let colX = tableX;
    headerCols.forEach(col => {
      slide.addText(col.text, {
        x: colX,
        y: curY,
        w: col.w,
        h: LIST_ROW_H,
        fontSize: 8,
        fontFace: 'Arial',
        bold: true,
        color: TEXT_PRIMARY,
        fill: { color: HEADER_BG },
        valign: 'middle',
        margin: [0, 0, 0, 6],
      });
      colX += col.w;
    });

    // Header bottom border
    slide.addShape('line' as PptxGenJS.ShapeType, {
      x: tableX,
      y: curY + LIST_ROW_H,
      w: LIST_TABLE_W,
      h: 0,
      line: { color: BORDER_COLOR, width: 0.75 },
    });

    curY += LIST_ROW_H;

    // Render rows
    for (const row of pageRows) {
      if (row.type === 'product') {
        slide.addText(`${row.product.name} (${row.count})`, {
          x: tableX,
          y: curY,
          w: LIST_TABLE_W,
          h: LIST_ROW_H,
          fontSize: 8,
          fontFace: 'Arial',
          bold: true,
          color: TEXT_PRIMARY,
          fill: { color: PRODUCT_BG },
          valign: 'middle',
          margin: [0, 0, 0, 6],
        });
      } else if (row.type === 'subproduct') {
        slide.addText(row.name, {
          x: tableX,
          y: curY,
          w: LIST_TABLE_W,
          h: LIST_ROW_H,
          fontSize: 7.5,
          fontFace: 'Arial',
          color: TEXT_SECONDARY,
          fill: { color: SUBPROD_BG },
          valign: 'middle',
          margin: [0, 0, 0, 14],
        });
      } else {
        const init = row.initiative;
        const clsColor = hex(init.classification?.color || '3B82F6');
        const statusColor = hex(init.status?.color || '6B7280');
        const hasDeps = init.dependencies && init.dependencies.length > 0;

        const startQ = quarters.find(q => q.key === init.startQuarter);
        const endQ = init.endQuarter ? quarters.find(q => q.key === init.endQuarter) : null;

        // Initiative name with optional dependency indicator
        const titleText = hasDeps ? `${init.title}  \u{1F517}` : init.title;
        slide.addText(titleText, {
          x: tableX,
          y: curY,
          w: LIST_COL_NAME_W,
          h: LIST_ROW_H,
          fontSize: 7.5,
          fontFace: 'Arial',
          color: TEXT_PRIMARY,
          valign: 'middle',
          margin: [0, 0, 0, 10],
        });

        // Classification: colored square + name
        const clsText: PptxGenJS.TextProps[] = [
          {
            text: '\u25A0 ',
            options: { fontSize: 8, color: clsColor, fontFace: 'Arial' },
          },
          {
            text: init.classification?.name || '',
            options: { fontSize: 7.5, color: TEXT_PRIMARY, fontFace: 'Arial' },
          },
        ];
        slide.addText(clsText, {
          x: tableX + LIST_COL_NAME_W,
          y: curY,
          w: LIST_COL_CLASS_W,
          h: LIST_ROW_H,
          valign: 'middle',
          margin: [0, 0, 0, 6],
        });

        // Status: dot + name
        const statusText: PptxGenJS.TextProps[] = [
          {
            text: '\u25CF ',
            options: { fontSize: 8, color: statusColor, fontFace: 'Arial' },
          },
          {
            text: init.status?.name || '',
            options: { fontSize: 7.5, color: TEXT_PRIMARY, fontFace: 'Arial' },
          },
        ];
        slide.addText(statusText, {
          x: tableX + LIST_COL_NAME_W + LIST_COL_CLASS_W,
          y: curY,
          w: LIST_COL_STATUS_W,
          h: LIST_ROW_H,
          valign: 'middle',
          margin: [0, 0, 0, 6],
        });

        // Start quarter
        slide.addText(startQ?.fullLabel || '', {
          x: tableX + LIST_COL_NAME_W + LIST_COL_CLASS_W + LIST_COL_STATUS_W,
          y: curY,
          w: LIST_COL_START_W,
          h: LIST_ROW_H,
          fontSize: 7.5,
          fontFace: 'Arial',
          color: TEXT_PRIMARY,
          valign: 'middle',
          margin: [0, 0, 0, 6],
        });

        // End quarter
        slide.addText(endQ?.fullLabel || '\u2013', {
          x: tableX + LIST_COL_NAME_W + LIST_COL_CLASS_W + LIST_COL_STATUS_W + LIST_COL_START_W,
          y: curY,
          w: LIST_COL_END_W,
          h: LIST_ROW_H,
          fontSize: 7.5,
          fontFace: 'Arial',
          color: TEXT_PRIMARY,
          valign: 'middle',
          margin: [0, 0, 0, 6],
        });

        // Row bottom border
        slide.addShape('line' as PptxGenJS.ShapeType, {
          x: tableX,
          y: curY + LIST_ROW_H,
          w: LIST_TABLE_W,
          h: 0,
          line: { color: 'F3F4F6', width: 0.25 },
        });
      }

      curY += LIST_ROW_H;
    }
  });
}

// ── Title Slide ─────────────────────────────────────────────────────────────────

function addTitleSlide(
  pptx: PptxGenJS,
  options: PptxExportOptions,
) {
  const slide = pptx.addSlide();
  slide.background = { fill: BG_WHITE };

  const viewName = options.viewType === 'timeline' ? 'Timeline' : 'List';

  slide.addText(`CrossProd \u2014 ${viewName} View`, {
    x: MARGIN,
    y: 2.0,
    w: SLIDE_W - MARGIN * 2,
    h: 0.8,
    fontSize: 28,
    fontFace: 'Arial',
    bold: true,
    color: TEXT_PRIMARY,
    align: 'center',
    valign: 'middle',
  });

  const filters = formatFilterSummary(options.filterSummary);
  slide.addText(`Filters: ${filters}`, {
    x: MARGIN + 1,
    y: 3.0,
    w: SLIDE_W - MARGIN * 2 - 2,
    h: 0.5,
    fontSize: 11,
    fontFace: 'Arial',
    color: TEXT_SECONDARY,
    align: 'center',
    valign: 'middle',
    wrap: true,
  });

  slide.addText(`${options.initiatives.length} Initiatives`, {
    x: MARGIN,
    y: 3.6,
    w: SLIDE_W - MARGIN * 2,
    h: 0.4,
    fontSize: 12,
    fontFace: 'Arial',
    color: TEXT_PRIMARY,
    align: 'center',
    valign: 'middle',
  });

  const exportDate = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  slide.addText(`Exported: ${exportDate}`, {
    x: MARGIN,
    y: 4.2,
    w: SLIDE_W - MARGIN * 2,
    h: 0.3,
    fontSize: 9,
    fontFace: 'Arial',
    color: TEXT_TERTIARY,
    align: 'center',
    valign: 'middle',
  });
}

// ── Main export function ────────────────────────────────────────────────────────

export async function exportAsPptx(
  options: PptxExportOptions,
): Promise<{ success: boolean; error?: string }> {
  try {
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_WIDE'; // 13.33 × 7.5
    pptx.author = 'CrossProd';
    pptx.subject = `${options.viewType === 'timeline' ? 'Timeline' : 'List'} View Export`;

    // Title slide
    addTitleSlide(pptx, options);

    // Content slides
    if (options.viewType === 'timeline') {
      buildTimelineSlides(pptx, options);
    } else {
      buildListSlides(pptx, options);
    }

    const timestamp = getTimestamp();
    const filename = `${options.viewType}_${timestamp}.pptx`;
    await pptx.writeFile({ fileName: filename });

    return { success: true };
  } catch (error) {
    console.error('PPTX export failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'PPTX export failed',
    };
  }
}
